import { useState } from 'react';
import Dialogo from '../../components/ui/Dialogo';
import { btnPrimario, btnSecundario, campo, pesos } from '../../components/ui/estilos';
import { useCategoriasMovimiento } from '../../hooks/useCategoriasMovimiento';

const n = (v: string | undefined) => parseFloat(v || '0') || 0;
const pie = (onCancelar: () => void, onOk: () => void, ok: string, deshabilitado = false) => (
  <div style={{ display: 'flex', gap: 10 }}>
    <button onClick={onCancelar} style={{ ...btnSecundario, flex: 1, height: 52 }}>Cancelar</button>
    <button onClick={onOk} disabled={deshabilitado} style={{ ...btnPrimario, flex: 2, height: 52, fontSize: 16, opacity: deshabilitado ? .5 : 1 }}>{ok}</button>
  </div>
);

export interface NuevoMovimiento { tipo: 'retiro' | 'deposito'; categoria: string; monto: number; motivo: string }

/* ─── Entrada / salida de efectivo. La contraseña se pide después (llave). ─── */
export function DialogoMovimiento({ tipo, enCaja, onContinuar, onCerrar }: {
  tipo: 'retiro' | 'deposito'; enCaja?: number; onContinuar: (m: NuevoMovimiento) => void; onCerrar: () => void;
}) {
  const cats = useCategoriasMovimiento();
  const [categoria, setCategoria] = useState(cats.defecto(tipo));
  const [motivo, setMotivo] = useState('');
  const [monto, setMonto] = useState('');
  const [error, setError] = useState('');
  const continuar = () => {
    if (!motivo.trim() || n(monto) <= 0) { setError('Captura motivo y monto.'); return; }
    // Aviso antes de pedir la contraseña; el servidor valida lo mismo.
    if (tipo === 'retiro' && enCaja !== undefined && n(monto) > enCaja + 0.005) {
      setError(`No hay suficiente efectivo: en caja debe haber ${pesos(Math.max(0, enCaja))}.`); return;
    }
    onContinuar({ tipo, categoria, monto: n(monto), motivo: motivo.trim() });
  };
  return (
    <Dialogo titulo={tipo === 'deposito' ? 'Registrar entrada de efectivo' : 'Registrar salida de efectivo'} onCerrar={onCerrar} ancho={440}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {cats.deTipo(tipo).map(c => (
          <button key={c.value} onClick={() => setCategoria(c.value)} aria-pressed={categoria === c.value}
            style={{ height: 44, padding: '0 14px', borderRadius: 999, fontSize: 15, fontWeight: 700, cursor: 'pointer',
              background: categoria === c.value ? 'var(--strong)' : 'var(--surface)', color: categoria === c.value ? 'var(--on-strong)' : 'var(--ink)',
              border: categoria === c.value ? 'none' : '1px solid var(--line-strong)' }}>
            {c.emoji} {c.label}
          </button>
        ))}
      </div>
      <input autoFocus placeholder={tipo === 'deposito' ? 'Motivo (ej. cambio del banco)' : 'Motivo (ej. compra de hielo)'} maxLength={120}
        value={motivo} onChange={e => setMotivo(e.target.value)} style={campo} aria-label="Motivo" />
      {tipo === 'retiro' && enCaja !== undefined && <div style={{ fontSize: 14, color: 'var(--muted)' }}>En caja debe haber {pesos(Math.max(0, enCaja))}</div>}
      <input type="number" min="0" inputMode="decimal" placeholder="Monto" value={monto} onChange={e => setMonto(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && continuar()} style={campo} aria-label="Monto" />
      {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      {pie(onCerrar, continuar, 'Registrar')}
    </Dialogo>
  );
}

/* ─── Cierre: lo que el sistema espera vs. lo que se contó, por método ─── */
export interface FilaCierre { clave: string; label: string; esperado: number }

export function DialogoCierre({ filas, onConfirmar, onCerrar }: {
  filas: FilaCierre[];
  onConfirmar: (declaracion: Record<string, number>, notas: string) => void;
  onCerrar: () => void;
}) {
  const [decl, setDecl] = useState<Record<string, string>>({});
  const [notas, setNotas] = useState('');
  const [error, setError] = useState('');
  const esperadoTotal = filas.reduce((s, f) => s + f.esperado, 0);
  const capturado = filas.some(f => (decl[f.clave] ?? '') !== '');
  const dif = filas.reduce((s, f) => s + n(decl[f.clave]), 0) - esperadoTotal;
  const cuadra = Math.abs(dif) < 0.005;

  const confirmar = () => {
    if ((decl.efectivo ?? '') === '') { setError('Captura el efectivo contado en caja (puede ser $0).'); return; }
    onConfirmar(Object.fromEntries(filas.map(f => [f.clave, n(decl[f.clave])])), notas.trim());
  };

  return (
    <Dialogo titulo="Cerrar turno" onCerrar={onCerrar} ancho={500}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 14px', borderRadius: 10, background: 'var(--well)' }}>
        {filas.map(f => (
          <div key={f.clave} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15 }}>
            <span style={{ color: 'var(--muted)' }}>{f.label}</span><span style={{ fontWeight: 700 }}>{pesos(f.esperado)}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, paddingTop: 6, borderTop: '1px solid var(--line-strong)' }}>
          <span>Sistema esperaba (total)</span><span style={{ fontWeight: 700 }}>{pesos(esperadoTotal)}</span>
        </div>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--muted)' }}>Cuenta lo real en caja/terminal por método</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filas.map(f => {
          const d = (decl[f.clave] ?? '') === '' ? null : n(decl[f.clave]) - f.esperado;
          return (
            <label key={f.clave} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ flex: 1, fontSize: 16, fontWeight: 700 }}>
                {f.label}{f.clave === 'efectivo' && <span style={{ color: 'var(--danger)' }}> *</span>}
                {d !== null && Math.abs(d) >= 0.005 && <span style={{ display: 'block', fontSize: 13, color: d > 0 ? 'var(--info)' : 'var(--danger)' }}>{d > 0 ? 'Sobra' : 'Falta'} {pesos(Math.abs(d))}</span>}
              </span>
              <input type="number" min="0" inputMode="decimal" placeholder="0.00" value={decl[f.clave] ?? ''}
                onChange={e => setDecl(p => ({ ...p, [f.clave]: e.target.value }))}
                style={{ ...campo, width: 160, height: 48, textAlign: 'right' }} />
            </label>
          );
        })}
      </div>
      {capturado && (
        <div aria-live="polite" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 17, fontWeight: 700, padding: '10px 14px', borderRadius: 10, background: 'var(--well)' }}>
          <span>{cuadra ? 'Cuadra exacto' : dif > 0 ? 'Sobrante' : 'Faltante'}</span>
          <span style={{ color: cuadra ? 'var(--ok)' : dif > 0 ? 'var(--info)' : 'var(--danger)' }}>{pesos(Math.abs(dif))}</span>
        </div>
      )}
      <textarea rows={2} placeholder="Notas del cierre (opcional)" value={notas} onChange={e => setNotas(e.target.value)}
        style={{ ...campo, height: 'auto', padding: '12px 16px', resize: 'none' }} aria-label="Notas del cierre" />
      {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      {pie(onCerrar, confirmar, 'Confirmar cierre')}
    </Dialogo>
  );
}
